import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Backdrop,
  Snackbar,
  InputAdornment,
  Link,
  SvgIcon,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Description as ProposalIcon,
  AttachMoney as MoneyIcon,
  ArrowBack as ArrowBackIcon,
  PictureAsPdf as PdfIcon,
  FolderShared as DriveIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Build as ServiceIcon,
  ExpandMore as ExpandMoreIcon,
  RemoveCircle as RemoveIcon,
  Schedule as ScheduleIcon,
  Percent as PercentIcon,
  Info as InfoIcon,
  Share as ShareIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
// Firebase imports
import { db } from '../firebase/config';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useConfig } from '../contexts/ConfigContext';
import PartnerRolesEditor from './common/PartnerRolesEditor';
import { PARTNER_POSITIONS, buildDefaultPartnerRoles } from '../utils/partnerRoles';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import NumericField from './NumericField';
import ClientAutocomplete from './tasks/ClientAutocomplete';
import { filterServiceOptions, frequencySearchText } from '../utils/serviceSearch';
import ucenaCoverBackground from '../assets/ucena-cover-background.png';
import { useClientGrouping, groupByClient, GroupByClientToggle, ClientGroupHeaderRow } from './ClientGroups';

const DuplicateIcon = (props) => (
  <SvgIcon {...props}>
    <path d="M6 17H3V3h13v2" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="8" y="7" width="13" height="14" rx="1" fill="none" stroke="currentColor" strokeWidth="1.7" strokeDasharray="1 2.5" strokeLinecap="round" />
    <path d="M11.5 14h6m-3-3v6" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </SvgIcon>
);

const statuses = [
  'Draft',
  'Needs Review',
  'Sent',
  'Dealmaking',
  'Ready to Send',
  'Meeting',
  'Accepted',
  'Rejected',
  'Contracted',
];
const TOTAL_POTENTIAL_STATUSES = new Set([
  'Draft',
  'Needs Review',
  'Sent',
  'Dealmaking',
  'Ready to Send',
  'Meeting',
]);
const REJECTED_STATUS = 'Rejected';
// Filter constants and helpers
const STATUS_ALL = '__ALL__';
const STATUS_RELEVANT = '__RELEVANT__';
const RELEVANT_EXCLUDED = new Set(['Rejected', 'Contracted']);
const CONTRACTED_STATUS = 'Contracted';
const NON_DELETABLE_STATUSES = new Set([CONTRACTED_STATUS, 'Accepted']);
const ACCEPTANCE_STATUSES = new Set(['Accepted', CONTRACTED_STATUS]);
const STATUS_ORDER = statuses.reduce((acc, s, i) => {
  acc[s] = i;
  return acc;
}, {});

// Rich text editor configuration
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    [{ color: [] }, { background: [] }],
    ['link'],
    ['clean'],
  ],
  clipboard: { matchVisual: false },
};

const quillFormats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'link',
  'color',
  'background',
  'align',
];

export default function Proposals() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userProfile, isSuperAdmin, isPartner, isMember } = useAuth();
  const { config, getCountryOptions, getPdfTexts, getBrandingConfig } = useConfig();
  const countries = getCountryOptions();
  const pdfTexts = getPdfTexts();
  const [proposals, setProposals] = useState([]);
  const [filteredProposals, setFilteredProposals] = useState([]);
  const [clients, setClients] = useState([]);
  const [costs, setCosts] = useState([]);
  const [entities, setEntities] = useState([]);
  const [selectedCostOption, setSelectedCostOption] = useState(null);
  // Newly added service: scrolled into view and briefly highlighted
  const [justAddedCost, setJustAddedCost] = useState(null);
  const justAddedCostRef = useRef(null);
  const clientGrouping = useClientGrouping('proposals.groupByClient');
  // Inline rename of a service title in the proposal form
  const [editingNameCost, setEditingNameCost] = useState(null);
  const [costNameDraft, setCostNameDraft] = useState('');
  useEffect(() => {
    if (!justAddedCost) return undefined;
    const frame = requestAnimationFrame(() => {
      justAddedCostRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    const timer = setTimeout(() => setJustAddedCost(null), 2000);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [justAddedCost]);
  const groupedSortedCosts = useMemo(() => {
    const list = Array.isArray(costs) ? costs.slice() : [];
    return list.sort((a, b) => {
      const ta = String(a?.category || 'Other').toLowerCase();
      const tb = String(b?.category || 'Other').toLowerCase();
      if (ta < tb) return -1;
      if (ta > tb) return 1;
      const na = String(a?.name || '').toLowerCase();
      const nb = String(b?.name || '').toLowerCase();
      return na.localeCompare(nb);
    });
  }, [costs]);
  // Vendor options not needed since vendor is displayed read-only next to price
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogTab, setDialogTab] = useState(0);
  const [editingProposal, setEditingProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [proposalToDuplicate, setProposalToDuplicate] = useState(null);
  const [duplicateError, setDuplicateError] = useState('');
  const duplicatingRef = useRef(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filters, setFilters] = useState({
    search: '',
    status: STATUS_RELEVANT,
    client: ''
  });
  const [formData, setFormData] = useState({
    title: '',
    client_id: '',
    valid_until: '',
    costs: [], // Will store deep copies of cost objects with quantities
    notes: '',
    timeline: '',
    partnerRoles: [],
    status: 'Draft'
  });
  
  const totalPotential = useMemo(() => {
    if (!Array.isArray(proposals)) return 0;
    return proposals.reduce((sum, proposal) => {
      if (!TOTAL_POTENTIAL_STATUSES.has(proposal?.status)) {
        return sum;
      }
      return sum + (Number(proposal?.totalAmount) || 0);
    }, 0);
  }, [proposals]);

  const acceptanceRate = useMemo(() => {
    if (!Array.isArray(proposals) || proposals.length === 0) return 0;
    const acceptedOrContracted = proposals.reduce((count, proposal) => (
      ACCEPTANCE_STATUSES.has(proposal?.status) ? count + 1 : count
    ), 0);
    const rejectedCount = proposals.reduce((count, proposal) => (
      proposal?.status === REJECTED_STATUS ? count + 1 : count
    ), 0);
    const denominator = acceptedOrContracted + rejectedCount;
    if (denominator === 0) return 0;
    return Math.round((acceptedOrContracted / denominator) * 100);
  }, [proposals]);

  const acceptedAndContractedCount = useMemo(() => {
    if (!Array.isArray(proposals)) return 0;
    return proposals.reduce((count, proposal) => (
      ACCEPTANCE_STATUSES.has(proposal?.status) ? count + 1 : count
    ), 0);
  }, [proposals]);

  // Read-only mode when viewing a contracted proposal
  const isViewOnly = Boolean(editingProposal && editingProposal.status === CONTRACTED_STATUS);

  const isClientActive = (client) => (client?.status || 'Active').toLowerCase() === 'active';
  const activeClients = useMemo(() => clients.filter(isClientActive), [clients]);
  // New-proposal client picker: active clients only, alphabetical. Editing shows the
  // existing client as a link (resolved from all clients), so inactive ones still display.
  const clientOptionsForDialog = useMemo(
    () => [...activeClients].sort((a, b) => (a.clientName || '').localeCompare(b.clientName || '')),
    [activeClients]
  );

  // Partner role positions (proposal stage)
  const partnerPositions = PARTNER_POSITIONS;

  // New proposals start from the Default Roles saved in App Configuration
  const getDefaultPartnerRoles = () => buildDefaultPartnerRoles(config?.defaultPartnerRoles, entities);

  // Default Valid Until for a new proposal: today + 15 calendar days (local date)
  const getDefaultValidUntil = () => {
    const d = new Date();
    d.setDate(d.getDate() + 15);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  // Helper function to create deep copy of cost
  const createDeepCopyCost = (masterCost, quantity = 1) => {
    const vendor = entities.find(e => e.id === masterCost.entity_id);
    const country = vendor ? countries.find(c => c.code === vendor.country) : null;
    const transportPercent = Number(country?.transportPercent) || 0;
    return {
      // Original cost data (deep copy)
      name: masterCost.name,
      description: masterCost.description || '',
      category: masterCost.category,
      execution_location: masterCost.execution_location,
      frequency: masterCost.frequency,
      internal_cost: masterCost.internal_cost,
      margin_percent: masterCost.margin_percent,
      advertising_budget: masterCost.advertising_budget || 0,
      advertising_budget_frequency: masterCost.advertising_budget_frequency || 'one_time',
      advertising_budget_enabled: Boolean(masterCost.advertising_budget_enabled),
      external_fee: masterCost.external_fee || 0,
      external_fee_frequency: masterCost.external_fee_frequency || 'one_time',
      external_fee_enabled: Boolean(masterCost.external_fee_enabled),
      entity_id: masterCost.entity_id || null,
      transport_percent: transportPercent,
      // Proposal-specific data
      quantity: quantity,
      original_cost_id: masterCost.id, // Reference for tracking purposes only
    };
  };

  // Firestore operations
  const fetchProposals = async () => {
    try {
      setLoading(true);
      const proposalsRef = collection(db, 'proposals');
      const q = query(proposalsRef, orderBy('created_at', 'desc'));
      
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const proposalsData = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          proposalsData.push({
            id: doc.id,
            ...data,
            created_at: data.created_at?.toDate?.()?.toISOString()?.split('T')[0] || new Date().toISOString().split('T')[0],
            lastModified: data.lastModified?.toDate?.()?.toISOString()?.split('T')[0] || new Date().toISOString().split('T')[0],
            costs: data.costs || [],
            totalAmount: data.totalAmount || 0,
          });
        });
        setProposals(proposalsData);
        setLoading(false);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error fetching proposals:', error);
      setSnackbar({ open: true, message: 'Error loading proposals', severity: 'error' });
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const clientsRef = collection(db, 'clients');
      
      let q;
      // For now, show all clients until we properly migrate the relationship manager IDs
      // TODO: Implement proper filtering once relationship manager IDs are migrated from user IDs to partner entity IDs
      q = query(clientsRef, orderBy('clientName', 'asc'));
      
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const clientsData = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          clientsData.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate?.()?.toISOString()?.split('T')[0] || new Date().toISOString().split('T')[0],
          });
        });
        setClients(clientsData);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error fetching clients:', error);
      setSnackbar({ open: true, message: 'Error loading clients', severity: 'error' });
      return null;
    }
  };

  const fetchCosts = async () => {
    try {
      const costsRef = collection(db, 'costs');
      const q = query(costsRef, orderBy('name', 'asc'));
      
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const costsData = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          costsData.push({
            id: doc.id,
            ...data,
          });
        });
        setCosts(costsData);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error fetching costs:', error);
      return null;
    }
  };

  const fetchEntities = async () => {
    try {
      const entitiesRef = collection(db, 'entities');
      const q = query(entitiesRef, orderBy('entityName', 'asc'));
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const entitiesData = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          entitiesData.push({ id: doc.id, ...data });
        });
        setEntities(entitiesData);
      });
      return unsubscribe;
    } catch (error) {
      console.error('Error fetching entities:', error);
      return null;
    }
  };

  const addProposal = async (proposalData) => {
    try {
      setSaving(true);
      const docRef = await addDoc(collection(db, 'proposals'), {
        ...proposalData,
        totalAmount: Number(proposalData.totalAmount) || 0,
        client_id: proposalData.client_id || null,
        costs: proposalData.costs || [], // Already deep copies
        partnerRoles: sanitizePartnerRoles(proposalData.partnerRoles, entities) || [],
        status: 'Draft',
        created_at: serverTimestamp(),
        created_by: currentUser.uid,
        lastModified: serverTimestamp(),
        shareEnabled: false,
        shareToken: null,
      });
      
      setSnackbar({ open: true, message: 'Proposal created successfully!', severity: 'success' });
      return docRef.id;
    } catch (error) {
      console.error('Error adding proposal:', error);
      setSnackbar({ open: true, message: 'Error creating proposal', severity: 'error' });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const updateProposal = async (proposalId, proposalData) => {
    try {
      setSaving(true);
      const proposalRef = doc(db, 'proposals', proposalId);
      await updateDoc(proposalRef, {
        ...proposalData,
        totalAmount: Number(proposalData.totalAmount) || 0,
        client_id: proposalData.client_id || null,
        costs: proposalData.costs || [], // Already deep copies
        partnerRoles: sanitizePartnerRoles(proposalData.partnerRoles, entities) || [],
        lastModified: serverTimestamp(),
      });
      
      setSnackbar({ open: true, message: 'Proposal updated successfully!', severity: 'success' });
    } catch (error) {
      console.error('Error updating proposal:', error);
      setSnackbar({ open: true, message: 'Error updating proposal', severity: 'error' });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const deleteProposal = async (proposalId) => {
    try {
      const proposalRef = doc(db, 'proposals', proposalId);
      await deleteDoc(proposalRef);
      setSnackbar({ open: true, message: 'Proposal deleted successfully!', severity: 'info' });
    } catch (error) {
      console.error('Error deleting proposal:', error);
      setSnackbar({ open: true, message: 'Error deleting proposal', severity: 'error' });
      throw error;
    }
  };

  const updateProposalStatus = async (proposalId, newStatus) => {
    try {
      const proposalRef = doc(db, 'proposals', proposalId);
      await updateDoc(proposalRef, {
        status: newStatus,
        lastModified: serverTimestamp(),
      });
      
      setSnackbar({ open: true, message: `Proposal status updated to ${newStatus}`, severity: 'success' });
    } catch (error) {
      console.error('Error updating proposal status:', error);
      setSnackbar({ open: true, message: 'Error updating proposal status', severity: 'error' });
    }
  };

  useEffect(() => {
    const unsubscribeProposals = fetchProposals();
    const unsubscribeClients = fetchClients();
    const unsubscribeCosts = fetchCosts();
    const unsubscribeEntities = fetchEntities();
    
    return () => {
      if (unsubscribeProposals && typeof unsubscribeProposals === 'function') {
        unsubscribeProposals();
      }
      if (unsubscribeClients && typeof unsubscribeClients === 'function') {
        unsubscribeClients();
      }
      if (unsubscribeCosts && typeof unsubscribeCosts === 'function') {
        unsubscribeCosts();
      }
      if (unsubscribeEntities && typeof unsubscribeEntities === 'function') {
        unsubscribeEntities();
      }
    };
  }, [currentUser]);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, proposals, clients, userProfile?.role]);

  // Auto-open when navigated with a proposal id via state or query string
  useEffect(() => {
    try {
      const params = new URLSearchParams(location?.search || '');
      const fromQuery = params.get('proposalId') || params.get('openProposalId');
      const maybeId = (location?.state && location.state.openProposalId) || fromQuery;
      if (maybeId && Array.isArray(proposals) && proposals.length > 0) {
        const found = proposals.find(p => p.id === maybeId);
        if (found) {
          handleOpenDialog(found);
        }
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state, location?.search, proposals]);

  const sortProposalsByClientThenTitle = (list) => {
    const normalize = (value) => String(value || '').trim().toLowerCase();
    return (Array.isArray(list) ? [...list] : []).sort((a, b) => {
      const clientA = normalize(getClientName(a?.client_id || a?.contactId));
      const clientB = normalize(getClientName(b?.client_id || b?.contactId));
      if (clientA < clientB) return -1;
      if (clientA > clientB) return 1;
      const titleA = normalize(a?.title);
      const titleB = normalize(b?.title);
      if (titleA < titleB) return -1;
      if (titleA > titleB) return 1;
      return 0;
    });
  };

  const applyFilters = () => {
    const hasClientFilter = Boolean(filters.client);
    let filtered = proposals;

    if (filters.status === STATUS_ALL) {
      // show all
      filtered = proposals;
    } else if (filters.status === STATUS_RELEVANT) {
      // exclude Rejected and Contracted, and sort
      filtered = proposals
        .filter((p) => !RELEVANT_EXCLUDED.has(p.status))
        .slice()
        .sort((a, b) => {
          const sa = STATUS_ORDER[a.status] ?? 999;
          const sb = STATUS_ORDER[b.status] ?? 999;
          if (sa !== sb) return sa - sb;
          // tie-breaker: newest first by created_at if available
          const ad = new Date(a.created_at || a.lastModified || 0).getTime();
          const bd = new Date(b.created_at || b.lastModified || 0).getTime();
          return bd - ad;
        });
    } else if (filters.status) {
      // specific status
      filtered = proposals.filter((p) => p.status === filters.status);
    }

    // Members only ever see Relevant statuses
    if (isMember()) {
      filtered = filtered.filter((p) => !RELEVANT_EXCLUDED.has(p.status));
    }

    if (hasClientFilter) {
      filtered = filtered.filter((proposal) => proposal.client_id === filters.client);
    }

    if (filters.search) {
      const term = filters.search.trim().toLowerCase();
      const clientNames = new Map(clients.map((client) => [client.id, String(client.clientName || '').toLowerCase()]));
      filtered = filtered.filter((proposal) => (
        String(proposal.title || '').toLowerCase().includes(term)
        || (clientNames.get(proposal.client_id || proposal.contactId) || '').includes(term)
      ));
    }

    filtered = sortProposalsByClientThenTitle(filtered);
    setFilteredProposals(filtered);
  };

  // Helper: transport percent for cost
  const getTransportPercentForCost = (cost) => {
    if (typeof cost.transport_percent === 'number') return cost.transport_percent;
    const vendor = entities.find(e => e.id === cost.entity_id);
    const country = vendor ? countries.find(c => c.code === vendor.country) : null;
    const pct = Number(country?.transportPercent) || 0;
    return pct < 0 ? 0 : pct;
  };

  // Calculate total amount = base services total (incl. transport) + partner roles uplift (based on internal cost only)
  const calculateTotalAmount = (proposalCosts, partnerRoles = []) => {
    const baseTotal = proposalCosts.reduce((total, cost) => {
      const internal = Number(cost.internal_cost) || 0;
      const marginPct = Number(cost.margin_percent) || 0;
      const transportPct = getTransportPercentForCost(cost);
      const clientPrice = internal * (1 + (marginPct / 100)) + internal * (transportPct / 100);
      return total + (clientPrice * (cost.quantity || 1));
    }, 0);
    const internalCostTotal = proposalCosts.reduce((total, cost) => {
      return total + ((Number(cost.internal_cost) || 0) * (cost.quantity || 1));
    }, 0);
    const totalPercent = Array.isArray(partnerRoles)
      ? partnerRoles.reduce((sum, r) => sum + (Number(r.percent) || 0), 0)
      : 0;
    const uplift = internalCostTotal * (totalPercent / 100);
    return baseTotal + uplift;
  };

  // Calculate total margin across all costs
  const calculateTotalMargin = (proposalCosts) => {
    return proposalCosts.reduce((total, cost) => {
      const marginAmount = cost.internal_cost * (cost.margin_percent / 100);
      return total + (marginAmount * (cost.quantity || 1));
    }, 0);
  };

  // Group costs by frequency for display (only one_time and monthly)
  const groupCostsByFrequency = (proposalCosts) => {
    const grouped = {
      monthly: proposalCosts.filter(cost => cost.frequency === 'monthly'),
      oneTime: proposalCosts.filter(cost => cost.frequency === 'one_time'),
    };
    return grouped;
  };

  // Sanitize partner roles before saving
  const sanitizePartnerRoles = (roles, entitiesList = entities) => {
    return (Array.isArray(roles) ? roles : []).map(role => ({
      positionId: role.positionId || '',
      positionName: role.positionName || '',
      entityId: role.entityId || null,
      entityName: role.entityId
        ? (entitiesList.find(e => e.id === role.entityId)?.entityName || role.entityName || '')
        : (role.entityName || ''),
      percent: role.entityId ? (Number(role.percent) || 0) : 0,
      editable: role.editable !== undefined ? role.editable : true
    }));
  };

  // Validation: all partner roles must be defined before accepting
  // A role is considered defined if it has an entity assigned OR it is explicitly set to "Nobody" with 0%
  const areAllPartnerRolesDefined = (roles, positions = partnerPositions) => {
    const roleList = Array.isArray(roles) ? roles : [];
    const hasAllPositions = positions.every(pos => roleList.some(r => r.positionId === pos.id));
    if (!hasAllPositions) return false;
    return roleList.every(r => {
      if (r && r.entityId) return true;
      const name = String(r?.entityName || '').trim().toLowerCase();
      const pct = Number(r?.percent) || 0;
      return name === 'nobody' && pct === 0;
    });
  };

  // Financial breakdown for the dialog's Financials tab
  const getFinancialBreakdown = (proposalCosts, partnerRoles = []) => {
    const totalPartnerPercent = Array.isArray(partnerRoles)
      ? partnerRoles.reduce((sum, r) => sum + (Number(r.percent) || 0), 0)
      : 0;

    const services = (proposalCosts || [])
      .filter(c => !c.isNew)
      .map((cost) => {
        const internal = Number(cost.internal_cost) || 0;
        const quantity = Number(cost.quantity) || 1;
        const marginPct = Number(cost.margin_percent) || 0;
        const transportPct = getTransportPercentForCost(cost);
        const perUnit = internal * (1 + (marginPct / 100)) + internal * (transportPct / 100);
        const baseTotal = perUnit * quantity;
        // Partner & company fees apply to internal cost and should be applied in both first and other months for monthly services
        const uplift = (internal * quantity) * (totalPartnerPercent / 100);
        const isMonthly = cost.frequency === 'monthly';
        return {
          name: getCostName(cost),
          frequency: cost.frequency,
          firstPaymentAmount: baseTotal + uplift,
          monthlyAmount: isMonthly ? (baseTotal + uplift) : 0,
        };
      });

    const firstPaymentTotal = services.reduce((sum, s) => sum + s.firstPaymentAmount, 0);
    const monthlyTotal = services.reduce((sum, s) => sum + s.monthlyAmount, 0);

    return { services, totals: { firstPaymentTotal, monthlyTotal } };
  };

  const handleOpenDialog = (proposal = null) => {
    if (proposal) {
      // Accepted proposals can be edited because they may lead to Contracting or changes requested by client.
      
      setEditingProposal(proposal);
      setFormData({
        title: proposal.title,
        client_id: proposal.client_id || proposal.contactId, // locked for existing proposals
        valid_until: proposal.valid_until || proposal.validUntil, // Handle legacy field
        costs: proposal.costs || [],
        notes: proposal.notes,
        timeline: proposal.timeline,
        partnerRoles: Array.isArray(proposal.partnerRoles) && proposal.partnerRoles.length > 0
          ? proposal.partnerRoles
          : getDefaultPartnerRoles(),
        status: proposal.status || 'Draft'
      });
    } else {
      setEditingProposal(null);
      setFormData({
        title: '',
        client_id: '',
        valid_until: getDefaultValidUntil(),
        costs: [],
        notes: '',
        timeline: '',
        partnerRoles: getDefaultPartnerRoles(),
        status: 'Draft'
      });
    }
    setDialogTab(0);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProposal(null);
  };

  const closeDuplicateDialog = () => {
    if (duplicatingRef.current) return;
    setProposalToDuplicate(null);
    setDuplicateError('');
  };

  const handleDuplicate = async (proposal) => {
    if (duplicatingRef.current || !currentUser || !proposal) return;
    duplicatingRef.current = true;
    setDuplicatingId(proposal.id);
    setDuplicateError('');
    try {
      // Copy the saved values without recalculating prices or replacing partner roles.
      const source = await getDoc(doc(db, 'proposals', proposal.id));
      if (!source.exists()) {
        setDuplicateError('This proposal no longer exists. Close this dialog and refresh the list.');
        return;
      }
      const duplicate = { ...source.data() };
      ['id', 'linkedContractId', 'lastModifiedAt', 'lastModifiedBy'].forEach((field) => {
        delete duplicate[field];
      });
      Object.assign(duplicate, {
        status: 'Draft',
        created_at: serverTimestamp(),
        created_by: currentUser.uid,
        lastModified: serverTimestamp(),
        shareEnabled: false,
        shareToken: null,
      });
      const newProposal = await addDoc(collection(db, 'proposals'), duplicate);
      setProposalToDuplicate(null);
      handleOpenDialog({ ...duplicate, id: newProposal.id });
      setSnackbar({ open: true, message: 'Proposal duplicated as a draft.', severity: 'success' });
    } catch (error) {
      console.error('Error duplicating proposal:', error);
      setDuplicateError('Unable to duplicate proposal. Please try again.');
    } finally {
      duplicatingRef.current = false;
      setDuplicatingId(null);
    }
  };

  const handleSubmit = async () => {
    try {
    // Basic validation
    if (!formData.title || !formData.title.trim()) {
      setSnackbar({ open: true, message: 'Proposal Title is required', severity: 'error' });
      return;
    }
    if (!formData.client_id) {
      setSnackbar({ open: true, message: 'Client is required', severity: 'error' });
      return;
    }
    if (!formData.valid_until) {
      setSnackbar({ open: true, message: 'Valid Until is required', severity: 'error' });
      return;
    }
    // Partner roles must be fully defined at all stages
    if (!areAllPartnerRolesDefined(formData.partnerRoles)) {
      setSnackbar({ open: true, message: 'Define all partner roles before saving. "Nobody" with 0% is allowed.', severity: 'warning' });
      return;
    }
    // Enforce Contracted status cannot be set here
    if (formData.status === CONTRACTED_STATUS && (!editingProposal || editingProposal.status !== CONTRACTED_STATUS)) {
      setSnackbar({ open: true, message: 'Contracted is set by Contract Management only', severity: 'warning' });
      return;
    }
    // If accepting, ensure all partner roles are defined
    if (formData.status === 'Accepted') {
      if (!areAllPartnerRolesDefined(formData.partnerRoles)) {
        setSnackbar({ open: true, message: 'Define all partner roles before accepting. "Nobody" with 0% is allowed.', severity: 'warning' });
        return;
      }
    }
    const totalAmount = calculateTotalAmount(formData.costs, formData.partnerRoles);
    const lockedClientId = editingProposal ? (editingProposal.client_id || editingProposal.contactId) : formData.client_id;
    const proposalData = {
      ...formData,
      client_id: lockedClientId,
      totalAmount,
    };

    if (editingProposal) {
        await updateProposal(editingProposal.id, {
          ...proposalData,
          status: formData.status,
        });
    } else {
        await addProposal(proposalData);
    }
      
    handleCloseDialog();
    } catch (error) {
      // Error handling is done in the respective functions
    }
  };

  const handleSave = async () => {
    try {
      // Basic validation
      if (!formData.title || !formData.title.trim()) {
        setSnackbar({ open: true, message: 'Proposal Title is required', severity: 'error' });
        return;
      }
      if (!formData.client_id) {
        setSnackbar({ open: true, message: 'Client is required', severity: 'error' });
        return;
      }
      if (!formData.valid_until) {
        setSnackbar({ open: true, message: 'Valid Until is required', severity: 'error' });
        return;
      }
      // Partner roles must be fully defined at all stages
      if (!areAllPartnerRolesDefined(formData.partnerRoles)) {
        setSnackbar({ open: true, message: 'Define all partner roles before saving. "Nobody" with 0% is allowed.', severity: 'warning' });
        return;
      }
      if (formData.status === CONTRACTED_STATUS && (!editingProposal || editingProposal.status !== CONTRACTED_STATUS)) {
        setSnackbar({ open: true, message: 'Contracted is set by Contract Management only', severity: 'warning' });
        return;
      }
      // If accepting, ensure all partner roles are defined
      if (formData.status === 'Accepted') {
        if (!areAllPartnerRolesDefined(formData.partnerRoles)) {
          setSnackbar({ open: true, message: 'Define all partner roles before accepting. "Nobody" with 0% is allowed.', severity: 'warning' });
          return;
        }
      }
      const totalAmount = calculateTotalAmount(formData.costs, formData.partnerRoles);
      const proposalData = {
        ...formData,
        totalAmount,
      };

      if (editingProposal) {
        await updateProposal(editingProposal.id, {
          ...proposalData,
          status: formData.status,
        });
      } else {
        const newId = await addProposal(proposalData);
        setEditingProposal({ ...proposalData, id: newId });
      }
    } catch (error) {
      // Error handling is done in the respective functions
    }
  };

  const handleDelete = async (id) => {
    const proposal = proposals.find((p) => p.id === id);
    if (proposal && NON_DELETABLE_STATUSES.has(proposal.status)) {
      const msg = proposal.status === 'Accepted'
        ? 'Accepted proposals cannot be deleted'
        : 'Contracted proposals cannot be deleted';
      setSnackbar({ open: true, message: msg, severity: 'warning' });
      return;
    }
    if (window.confirm('Are you sure you want to delete this proposal?')) {
      try {
        await deleteProposal(id);
      } catch (error) {
        // Error handling is done in the deleteProposal function
      }
    }
  };

  const handleAddCost = () => {
    if (isViewOnly) return;
    // Add a placeholder cost that will be populated when user selects from dropdown
    setFormData({
      ...formData,
      costs: [...formData.costs, { costId: '', quantity: 1, isNew: true }]
    });
  };

  const handleCostChange = (index, field, value) => {
    if (isViewOnly) return;
    const newCosts = [...formData.costs];
    
    if (field === 'costId') {
      // User selected a cost from master list - create deep copy
      const masterCost = costs.find(c => c.id === value);
      if (masterCost) {
        const deepCopyCost = createDeepCopyCost(masterCost, newCosts[index].quantity || 1);
        newCosts[index] = deepCopyCost;
      }
    } else if (field === 'quantity') {
      newCosts[index].quantity = Number(value) || 1;
    } else {
      // Handle other editable fields (margin, prices, etc.)
      newCosts[index][field] = field.includes('cost') || field.includes('budget') || field.includes('fee') || field.includes('percent') ? 
        Number(value) || 0 : value;
    }
    
    setFormData({ ...formData, costs: newCosts });
  };

  const handleRemoveCost = (index) => {
    if (isViewOnly) return;
    const newCosts = formData.costs.filter((_, i) => i !== index);
    setFormData({ ...formData, costs: newCosts });
  };

  const handleStatusChange = (proposalId, newStatus) => {
    const current = proposals.find((p) => p.id === proposalId)?.status;
    if (current === CONTRACTED_STATUS && newStatus !== CONTRACTED_STATUS) {
      setSnackbar({ open: true, message: 'Status is frozen for Contracted proposals', severity: 'warning' });
      return;
    }
    if (newStatus === CONTRACTED_STATUS && current !== CONTRACTED_STATUS) {
      setSnackbar({ open: true, message: 'Contracted is set by Contract Management only', severity: 'warning' });
      return;
    }
    if (newStatus === 'Accepted') {
      const proposal = proposals.find((p) => p.id === proposalId);
      const roles = proposal?.partnerRoles || [];
      if (!areAllPartnerRolesDefined(roles)) {
        setSnackbar({ open: true, message: 'Define all partner roles before accepting. "Nobody" with 0% is allowed.', severity: 'warning' });
        return;
      }
    }
    updateProposalStatus(proposalId, newStatus);
  };

  const stripHtml = (html) => {
    if (!html) return '';
    const text = html
      .replace(/<\/(p|div|li|h1|h2|h3|h4|h5|h6)>/g, '\n')
      .replace(/<br\s*\/?>(\s*\n)*/gi, '\n')
      .replace(/<li>/g, '• ')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
    return text.replace(/\n{3,}/g, '\n\n').trim();
  };

  const formatHumanDate = (value, opts = { includeDay: true }) => {
    if (!value) return '';
    let d = null;
    if (typeof value?.toDate === 'function') d = value.toDate();
    if (!d && value instanceof Date) d = value;
    if (!d && typeof value === 'string') {
      d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : `${value}-01T00:00:00`);
    }
    if (!d || isNaN(d.getTime())) return String(value);
    const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const day = d.getDate();
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    return opts.includeDay ? `${day} ${month}, ${year}` : `${month} ${year}`;
  };

  const getRoleEntityName = (roles, positionId) => {
    const r = (Array.isArray(roles) ? roles : []).find(x => x.positionId === positionId);
    return r?.entityName || '';
  };

  const generateProposalPDF = async (proposal) => {
    const doc = new jsPDF('p', 'pt', 'a4');
    const margin = 50;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const usableWidth = pageWidth - margin * 2;
    const UCENA_RED = { r: 244, g: 67, b: 54 }; // #F44336
    const clientName = getClientName(proposal.client_id || proposal.contactId);
    const proposalTitle = proposal.title || 'Project';
    const validUntil = proposal.valid_until || proposal.validUntil || '';
    const projectLead = getRoleEntityName(proposal.partnerRoles, 'client_pm') || '';
    const ucenaLead = getRoleEntityName(proposal.partnerRoles, 'team_pm') || userProfile?.displayName || '';

    const loadImage = (src) => new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
      img.src = src;
    });

    const drawCoverBackground = async () => {
      try {
        const bg = await loadImage(ucenaCoverBackground);
        const bgProps = doc.getImageProperties(bg);
        const scale = Math.max(pageWidth / bgProps.width, pageHeight / bgProps.height);
        const bgWidth = bgProps.width * scale;
        const bgHeight = bgProps.height * scale;
        const bgX = (pageWidth - bgWidth) / 2;
        const bgY = (pageHeight - bgHeight) / 2;
        doc.addImage(bg, 'PNG', bgX, bgY, bgWidth, bgHeight);
        return true;
      } catch (e) {
        console.warn('Could not load cover background for PDF', e);
        doc.setFillColor(UCENA_RED.r, UCENA_RED.g, UCENA_RED.b);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');
        return false;
      }
    };

    // --- COVER PAGE ---
    const coverBackgroundLoaded = await drawCoverBackground();

    // Load Logo only when falling back to the legacy solid background.
    if (!coverBackgroundLoaded) {
      try {
        const branding = getBrandingConfig();
        const logoSrc = branding.logo || '/assets/logo.png';

        const img = await loadImage(logoSrc);
        const imgProps = doc.getImageProperties(img);
        const logoHeight = 30;
        const logoWidth = (imgProps.width * logoHeight) / imgProps.height;
        doc.addImage(img, 'PNG', margin, margin, logoWidth, logoHeight);
      } catch (e) {
        console.warn('Could not load logo for PDF', e);
        // Fallback text
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(24);
        doc.text("UCENA", margin, margin + 24);
      }
    }

    // Title: Marketing Proposal
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(48);
    const titleY = pageHeight * 0.37;
    doc.text("MARKETING", margin, titleY);
    doc.text("PROPOSAL", margin, titleY + 58);

    // Prepared by
    doc.setFont('times', 'roman');
    doc.setFontSize(15);
    const coverInfoY = pageHeight * 0.675;
    doc.text("Prepared by:", margin, coverInfoY);
    doc.text("UCENA Marketing Team", margin, coverInfoY + 22);
    
    // Footer Info
    doc.setFont('times', 'roman');
    doc.setFontSize(14);
    const footerY = pageHeight - margin;
    doc.text("www.ucena.com", margin, footerY - 48);
    doc.text("info@ucena.com", margin, footerY - 24);
    doc.text("30 North Gould Street, Sheridan, WY 82801", margin, footerY);

    // --- PAGE 2: Content ---
    doc.addPage();
    // Light Gray Background for content pages
    doc.setFillColor(240, 240, 240); // #F0F0F0
    doc.rect(0, 0, pageWidth, pageHeight, 'F');
    doc.setTextColor(0, 0, 0); // Reset to black

    let y = margin;

    // 1. Heading: Client Name | Proposal Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text(`${clientName} | ${proposalTitle}`, pageWidth / 2, y + 20, { align: 'center' });
    y += 50;

    // 2. Intro Text
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    const intro = (pdfTexts?.proposals?.intro) || `At UCENA, we don't just offer services—we become partners in bringing your vision to life. With a sharp focus on product innovation, marketing strategy, and creative execution, we work alongside you to craft solutions that don't just meet expectations but redefine them.`;
    const introLines = doc.splitTextToSize(intro, usableWidth);
    introLines.forEach((row) => {
      doc.text(String(row), margin, y);
      y += 16;
    });
    y += 30;

    // 3. Metadata Box (UCENA Red)
    const metaHeight = 75;
    doc.setFillColor(UCENA_RED.r, UCENA_RED.g, UCENA_RED.b);
    doc.rect(0, y, pageWidth, metaHeight, 'F');
    
    // Metadata Text
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    
    const col1X = margin + 20;
    const col2X = pageWidth / 2 + 20;
    const rowY = y + 45;

    // Col 1
    doc.setFont('helvetica', 'bold');
    doc.text('Project Lead', col1X, rowY);
    doc.setFont('helvetica', 'normal');
    doc.text(`- ${projectLead || '—'}`, col1X + 75, rowY);

    // Col 2
    doc.setFont('helvetica', 'bold');
    doc.text('Valid Until', col2X, rowY);
    doc.setFont('helvetica', 'normal');
    doc.text(`- ${formatHumanDate(validUntil)}`, col2X + 65, rowY);

    y += metaHeight + 40;
    doc.setTextColor(0, 0, 0); // Reset text color

    // Helper for page breaks
    const checkPageBreak = (heightNeeded) => {
      if (y + heightNeeded > pageHeight - margin) {
        doc.addPage();
        // Re-apply background
        doc.setFillColor(240, 240, 240);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');
        doc.setTextColor(0, 0, 0);
        y = margin + 20;
      }
    };

    // 4. SUMMARY
    checkPageBreak(100);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text("SUMMARY", pageWidth / 2, y, { align: 'center' });
    y += 30;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    const summaryText = stripHtml(proposal.notes);
    const summaryLines = doc.splitTextToSize(summaryText, usableWidth);
    summaryLines.forEach((row) => {
      checkPageBreak(16);
      doc.text(String(row), margin, y);
      y += 16;
    });
    y += 30;

    // 5. KEY OPPORTUNITIES
    checkPageBreak(100);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text("KEY OPPORTUNITIES", pageWidth / 2, y, { align: 'center' });
    y += 30;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    const additionalText = stripHtml(proposal.timeline);
    const additionalLines = doc.splitTextToSize(additionalText, usableWidth);
    additionalLines.forEach((row) => {
      checkPageBreak(16);
      doc.text(String(row), margin, y);
      y += 16;
    });
    y += 50;


    // --- SERVICES AND BUDGET ---
    checkPageBreak(200); // Ensure header starts on a page with some room
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text("SERVICES AND BUDGET", pageWidth / 2, y, { align: 'center' });
    y += 40;

    const proposalServices = (proposal.costs || []).filter(c => !c.isNew);
    
    proposalServices.forEach((service, index) => {
      checkPageBreak(60);
      
      // Service Title
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.text(service.name || getCostName(service), margin, y);
      y += 16;

      // Service Description (Bulleted)
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const descText = stripHtml(service.description || '');
      // Simple bullet splitting if needed, or just text block
      const descLines = doc.splitTextToSize(descText, usableWidth - 20);
      descLines.forEach((line) => {
        checkPageBreak(14);
        doc.text(line, margin + 10, y);
        y += 14;
      });

      y += 10;
      // Separator Line
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.5);
      doc.line(margin, y, pageWidth - margin, y);
      y += 20;
    });

    y += 20;

    // FEES Section
    const { totals } = getFinancialBreakdown(proposal.costs || [], proposal.partnerRoles || []);
    const firstMonth = totals.firstPaymentTotal || 0;
    const months23 = totals.monthlyTotal || 0;

    checkPageBreak(150);
    
    // First Month Fee
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(0, 0, 0);
    doc.text("FIRST MONTH'S FEE", margin, y);
    doc.text(formatCurrency(firstMonth), pageWidth - margin, y, { align: 'right' });
    y += 10;
    
    // Thick Line
    doc.setDrawColor(UCENA_RED.r, UCENA_RED.g, UCENA_RED.b);
    doc.setLineWidth(1.5);
    doc.line(margin, y, pageWidth - margin, y);
    y += 40;

    // Other Months Fee
    doc.text("OTHER MONTHS", margin, y);
    doc.text(formatCurrency(months23), pageWidth - margin, y, { align: 'right' });
    y += 10;
    
    // Thick Line
    doc.line(margin, y, pageWidth - margin, y);
    y += 30;


    // Footer page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      if (i === 1) continue; 
      
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFontSize(9);
      doc.setTextColor(150);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 20, { align: 'right' });
    }

    const safe = (s) => (s || '').replace(/[^a-z0-9\-_]+/gi, '_').slice(0, 60);
    const filename = `Proposal_${safe(clientName)}_${safe(proposalTitle)}.pdf`;
    doc.save(filename);
  };

  const handleGeneratePDF = async (proposal) => {
    try {
      await generateProposalPDF(proposal);
    } catch (e) {
      console.error('Error generating PDF', e);
      setSnackbar({ open: true, message: 'Error generating PDF', severity: 'error' });
    }
  };

  const generateShareToken = () => {
    try {
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
    } catch {
      return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
    }
  };

  const buildShareLink = (proposalId, token) => {
    const origin = window.location.origin;
    return `${origin}/p/${encodeURIComponent(proposalId)}/${encodeURIComponent(token)}`;
  };

  // Copies the share link and opens it in a new tab, keeping this page in place
  const handleShareClick = async (proposal) => {
    const needsToken = !proposal.shareEnabled || !proposal.shareToken;
    const token = needsToken ? generateShareToken() : proposal.shareToken;
    const link = buildShareLink(proposal.id, token);

    // Both must start inside the click: the clipboard needs this page focused,
    // and popup blockers only allow tabs opened directly from a click.
    const copied = navigator.clipboard
      ? navigator.clipboard.writeText(link).then(() => true, () => false)
      : Promise.resolve(false);
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;

    try {
      if (needsToken) {
        const ref = doc(db, 'proposals', proposal.id);
        await updateDoc(ref, { shareEnabled: true, shareToken: token, lastModified: serverTimestamp() });
      }
    } catch (e) {
      console.error('Error creating share link', e);
      if (tab) tab.close();
      setSnackbar({ open: true, message: 'Error creating share link', severity: 'error' });
      return;
    }

    if (tab) tab.location.href = link;
    const wasCopied = await copied;
    setSnackbar({
      open: true,
      message: wasCopied
        ? (tab ? 'Share link copied and opened in a new tab' : 'Share link copied (allow pop-ups to open it in a new tab)')
        : (tab ? 'Share link opened in a new tab (copy failed)' : 'Could not copy or open the share link'),
      severity: wasCopied && tab ? 'success' : 'warning'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Draft': return 'default';
      case 'Sent': return 'info';
      case 'Accepted': return 'success';
      case 'Rejected': return 'error';
      case 'Expired': return 'warning';
      default: return 'default';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.clientName : 'Unknown Client';
  };

  const getClientDriveUrl = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.googleDriveURL || '';
  };

  // For deep copy costs, name is stored directly in the cost object
  const getCostName = (cost) => {
    if (typeof cost === 'string') {
      // Legacy support - cost is an ID
      const costData = costs.find(c => c.id === cost);
      return costData ? costData.name : 'Unknown Service';
    }
    // New structure - cost is an object with name property
    return cost.name || 'Unknown Service';
  };

  const renderProposalRow = (proposal) => (
    <TableRow key={proposal.id} hover>
      <TableCell>
        <Box>
          <Typography variant="body2" fontWeight="bold">
            {proposal.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Created: {proposal.created_at}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        {(() => {
          const url = getClientDriveUrl(proposal.client_id || proposal.contactId);
          return url ? (
            <Tooltip title="Open Google Drive">
              <IconButton
                size="small"
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
              >
                <DriveIcon />
              </IconButton>
            </Tooltip>
          ) : null;
        })()}
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PersonIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
          <Link
            href={`/clients?clientId=${encodeURIComponent(proposal.client_id || proposal.contactId || '')}`}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
          >
            {getClientName(proposal.client_id || proposal.contactId)}
          </Link>
        </Box>
      </TableCell>
      <TableCell>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <Select
            value={proposal.status}
            onChange={(e) => handleStatusChange(proposal.id, e.target.value)}
            variant="outlined"
            disabled={proposal.status === CONTRACTED_STATUS}
          >
            {(() => {
              const allowed = proposal.status === CONTRACTED_STATUS
                ? [CONTRACTED_STATUS]
                : statuses.filter((s) => s !== CONTRACTED_STATUS);
              const options = [...new Set([proposal.status, ...allowed])];
              return options.map((status) => (
                <MenuItem key={status} value={status}>{status}</MenuItem>
              ));
            })()}
          </Select>
        </FormControl>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* <MoneyIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} /> */}
          {proposal.costs.length} 
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body1" fontWeight="bold" color="primary">
          {formatCurrency(proposal.totalAmount)}
        </Typography>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CalendarIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
          {proposal.valid_until || proposal.validUntil}
        </Box>
      </TableCell>
      <TableCell>
        <Tooltip title="Generate PDF">
          <IconButton
            size="small"
            onClick={() => handleGeneratePDF(proposal)}
            color="secondary"
          >
            <PdfIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Share link">
          <IconButton
            size="small"
            onClick={() => handleShareClick(proposal)}
            color="primary"
          >
            <ShareIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Duplicate proposal">
          <span>
            <IconButton
              size="small"
              aria-label={`Duplicate proposal ${proposal.title || ''}`}
              onClick={() => { setProposalToDuplicate(proposal); setDuplicateError(''); }}
              disabled={duplicatingId !== null || !currentUser}
              color="primary"
            >
              {duplicatingId === proposal.id ? <CircularProgress size={20} /> : <DuplicateIcon />}
            </IconButton>
          </span>
        </Tooltip>
        {proposal.status === CONTRACTED_STATUS ? (
          <Tooltip title="View">
            <IconButton
              size="small"
              onClick={() => handleOpenDialog(proposal)}
              color="primary"
            >
              <InfoIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => handleOpenDialog(proposal)}
                color="primary"
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            {proposal.status !== 'Accepted' && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={() => handleDelete(proposal.id)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
      </TableCell>
    </TableRow>
  );

  return (
    <Container maxWidth={false} sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minHeight: 0 }}>
     

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ProposalIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              Proposals Management
            </Typography>
          
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <GroupByClientToggle grouped={clientGrouping.grouped} onChange={clientGrouping.setGrouped} />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Create Proposal
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="search"
                label="Search Proposals or Clients"
                placeholder="Search by proposal title or client name"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <MenuItem value={STATUS_RELEVANT}>Relevant</MenuItem>
                  {!isMember() && <MenuItem value={STATUS_ALL}>All Statuses</MenuItem>}
                  {statuses.filter(status => !isMember() || !RELEVANT_EXCLUDED.has(status)).map(status => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Client</InputLabel>
                <Select
                  value={filters.client}
                  label="Client"
                  onChange={(e) => setFilters({ ...filters, client: e.target.value })}
                >
                  <MenuItem value="">All Clients</MenuItem>
                  {clients.map(client => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.clientName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
      </Paper>

      {/* Proposals Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden', mb: 3, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <TableContainer sx={{ flex: 1, minHeight: 0 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Proposal</TableCell>
                <TableCell>Drive</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Services</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Valid Until</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredProposals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="h6" color="text.secondary">No proposals found.</Typography>
                  </TableCell>
                </TableRow>
              ) : clientGrouping.grouped ? (
                groupByClient(
                  filteredProposals,
                  (proposal) => proposal.client_id || proposal.contactId,
                  getClientName
                ).map((group) => (
                  <React.Fragment key={`group-${group.key}`}>
                    <ClientGroupHeaderRow
                      group={group}
                      colSpan={8}
                      collapsed={clientGrouping.collapsed.has(group.key)}
                      onToggle={() => clientGrouping.toggleCollapsed(group.key)}
                      total={formatCurrency(group.items.reduce((sum, p) => sum + (Number(p.totalAmount) || 0), 0))}
                    />
                    {!clientGrouping.collapsed.has(group.key) && group.items.map(renderProposalRow)}
                  </React.Fragment>
                ))
              ) : (
                filteredProposals.map(renderProposalRow)
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={proposalToDuplicate !== null} onClose={closeDuplicateDialog} maxWidth="xs" fullWidth
        aria-labelledby="duplicate-proposal-title" aria-describedby="duplicate-proposal-description">
        <DialogTitle id="duplicate-proposal-title">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DuplicateIcon color="primary" />
            Duplicate proposal?
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="duplicate-proposal-description" sx={{ overflowWrap: 'anywhere' }}>
            Are you sure you want to duplicate “{proposalToDuplicate?.title || 'Untitled proposal'}”?
          </DialogContentText>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            A new draft will be created with the same proposal details.
          </Typography>
          {duplicateError && <Alert severity="error" sx={{ mt: 2 }}>{duplicateError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={closeDuplicateDialog} disabled={duplicatingId !== null}>Cancel</Button>
          <Button variant="contained" onClick={() => handleDuplicate(proposalToDuplicate)}
            disabled={duplicatingId !== null || !currentUser}
            startIcon={duplicatingId !== null ? <CircularProgress size={18} color="inherit" /> : <DuplicateIcon />}>
            {duplicatingId !== null ? 'Duplicating…' : 'Duplicate proposal'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editingProposal ? (isViewOnly ? 'View Proposal' : 'Edit Proposal') : 'Create Proposal'}
        </DialogTitle>
        <DialogContent>
          <Tabs value={dialogTab} onChange={(e, v) => setDialogTab(v)} sx={{ mb: 2 }}>
            <Tab label="Details" />
            <Tab label="Services" />
            <Tab label="Partner Roles" />
            <Tab label="Financials" />
          </Tabs>

          {dialogTab === 0 && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Basic Information */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Proposal Title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  disabled={isViewOnly}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                {editingProposal ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ mr: 1 }}>Client:</Typography>
                    <Link
                      href={`/clients?clientId=${encodeURIComponent(formData.client_id || '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                    >
                      {getClientName(formData.client_id)}
                    </Link>
                  </Box>
                ) : (
                  <ClientAutocomplete
                    clients={clientOptionsForDialog}
                    value={formData.client_id || null}
                    onChange={(clientId) => setFormData({ ...formData, client_id: clientId || '' })}
                    label="Client"
                    placeholder="Search clients"
                    size="medium"
                    sx={{ width: '100%' }}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Valid Until"
                  type="date"
                  value={formData.valid_until}
                  onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                  disabled={isViewOnly}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    disabled={isViewOnly}
                  >
                    {(() => {
                      const allowed = isViewOnly
                        ? [CONTRACTED_STATUS]
                        : statuses.filter((s) => s !== CONTRACTED_STATUS);
                      const options = [...new Set([formData.status, ...allowed])];
                      return options.map((status) => (
                        <MenuItem key={status} value={status}>{status}</MenuItem>
                      ));
                    })()}
                  </Select>
                </FormControl>
              </Grid>
              {/* Total moved to Financials tab */}

              {/* Additional Information */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Summary</Typography>
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 2,
                    '& .ql-toolbar': { borderTopLeftRadius: 4, borderTopRightRadius: 4 },
                    '& .ql-container': {
                      borderBottomLeftRadius: 4,
                      borderBottomRightRadius: 4,
                      minHeight: 220,
                    },
                    '& .ql-editor': { minHeight: 180 },
                  }}
                >
                  <ReactQuill
                    theme="snow"
                    value={formData.notes}
                    onChange={(content) => setFormData({ ...formData, notes: content })}
                    modules={quillModules}
                    formats={quillFormats}
                    readOnly={isViewOnly}
                    placeholder="Write a detailed summary..."
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Key Opportunities</Typography>
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    '& .ql-toolbar': { borderTopLeftRadius: 4, borderTopRightRadius: 4 },
                    '& .ql-container': {
                      borderBottomLeftRadius: 4,
                      borderBottomRightRadius: 4,
                      minHeight: 260,
                    },
                    '& .ql-editor': { minHeight: 220 },
                  }}
                >
                  <ReactQuill
                    theme="snow"
                    value={formData.timeline}
                    onChange={(content) => setFormData({ ...formData, timeline: content })}
                    modules={quillModules}
                    formats={quillFormats}
                    readOnly={isViewOnly}
                    placeholder="Project timeline, phases, milestones..."
                  />
                </Box>
              </Grid>
            </Grid>
          )}

          {dialogTab === 1 && (
            <Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Service Structure</Typography>
              </Box>
              {!isViewOnly && (
                <Box sx={{ mb: 2 }}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={groupedSortedCosts}
                    // groupBy={(option) => String(option?.category || 'Other')}
                    value={selectedCostOption}
                    onChange={(_, newValue) => setSelectedCostOption(newValue || null)}
                    filterOptions={(opts, { inputValue }) =>
                      filterServiceOptions(opts, inputValue, (option) => {
                        const price =
                          typeof option?.internal_cost === 'number' && typeof option?.margin_percent === 'number'
                            ? option.internal_cost * (1 + option.margin_percent / 100)
                            : null;
                        return [
                          option?.name,
                          option?.category,
                          option?.serviceType,
                          frequencySearchText(option?.frequency),
                          entities.find((e) => e.id === option?.entity_id)?.entityName,
                          option?.description,
                          option?.execution_location,
                          price !== null ? `${formatCurrency(price)} ${price.toFixed(2)}` : null,
                        ];
                      })
                    }
                    getOptionLabel={(option) => {
                      const name = option?.name || '';
                      const category = option?.category || '';
                      const freqRaw = option?.frequency || '';
                      const period = String(freqRaw).replaceAll('_', ' ');
                      const vendorName = (() => {
                        try {
                          return entities.find((e) => e.id === option?.entity_id)?.entityName || '';
                        } catch {
                          return '';
                        }
                      })();
                      const price =
                        typeof option?.internal_cost === 'number' &&
                        typeof option?.margin_percent === 'number'
                          ? option.internal_cost * (1 + option.margin_percent / 100)
                          : null;
                      const base = [category, name, period, vendorName].filter(Boolean).join(' - ');
                      return price !== null ? `${base} - ${formatCurrency(price)}` : base;
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Add Service"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {params.InputProps.endAdornment}
                              <Button
                                variant="outlined"
                                size="small"
                                sx={{ ml: 1, whiteSpace: 'nowrap' }}
                                onClick={() => {
                                  if (!selectedCostOption) {
                                    setSnackbar({ open: true, message: 'Select a service', severity: 'warning' });
                                    return;
                                  }
                                  const deepCopyCost = createDeepCopyCost(selectedCostOption, 1);
                                  setFormData({
                                    ...formData,
                                    costs: [...(formData.costs || []), deepCopyCost]
                                  });
                                  setJustAddedCost(deepCopyCost);
                                  setSelectedCostOption(null);
                                  setSnackbar({ open: true, message: 'Service added to proposal', severity: 'success' });
                                }}
                                disabled={!selectedCostOption}
                                startIcon={<AddIcon />}
                              >
                                Add
                              </Button>
                            </>
                          )
                        }}
                      />
                    )}
                  />
                </Box>
              )}

              {/* Cost Selection flow simplified: use top Add Service selector. */}

              {(() => {
                const groupedCosts = groupCostsByFrequency(formData.costs.filter(c => !c.isNew));
                const totalMargin = calculateTotalMargin(formData.costs.filter(c => !c.isNew));
                
                const renderFrequencyChips = (current) => {
                  const label = current === 'monthly' ? 'Monthly' : 'One-time';
                  const color = current === 'monthly' ? 'info' : 'success';
                  return (
                    <Chip label={label} color={color} size="small" />
                  );
                };

                const getCostPriceTotal = (cost) => {
                  const internal = Number(cost.internal_cost) || 0;
                  const marginPct = Number(cost.margin_percent) || 0;
                  const transportPct = getTransportPercentForCost(cost);
                  const perUnit = internal * (1 + marginPct / 100) + internal * (transportPct / 100);
                  return perUnit * (Number(cost.quantity) || 1);
                };

                // Service name with term + price on the right
                const startEditingCostName = (cost) => {
                  setEditingNameCost(cost);
                  setCostNameDraft(cost.name || '');
                };
                const cancelEditingCostName = () => {
                  setEditingNameCost(null);
                  setCostNameDraft('');
                };
                const saveCostName = (cost) => {
                  const trimmed = costNameDraft.trim();
                  if (trimmed && trimmed !== cost.name) {
                    const costIndex = formData.costs.findIndex(c => c === cost);
                    handleCostChange(costIndex, 'name', trimmed);
                  }
                  cancelEditingCostName();
                };

                const renderCostName = (cost) => {
                  if (editingNameCost === cost && !isViewOnly) {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1 }}>
                        <TextField
                          size="small"
                          fullWidth
                          autoFocus
                          value={costNameDraft}
                          onChange={(e) => setCostNameDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              saveCostName(cost);
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              e.stopPropagation();
                              cancelEditingCostName();
                            }
                          }}
                        />
                        <Tooltip title="Save">
                          <span>
                            <IconButton size="small" color="primary" onClick={() => saveCostName(cost)} disabled={!costNameDraft.trim()}>
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton size="small" onClick={cancelEditingCostName}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    );
                  }
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ minWidth: 0 }}>{cost.name}</Typography>
                      {!isViewOnly && (
                        <Tooltip title="Edit title">
                          <IconButton size="small" onClick={() => startEditingCostName(cost)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  );
                };

                const renderCostHeader = (cost) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    {renderCostName(cost)}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                      {renderFrequencyChips(cost.frequency)}
                      <Typography variant="h6" color="primary" sx={{ whiteSpace: 'nowrap' }}>
                        {formatCurrency(getCostPriceTotal(cost))}
                      </Typography>
                    </Box>
                  </Box>
                );

                const renderEditableCostRow = (cost) => {
                  const hasExternal = cost.external_fee_enabled !== false;
                  const hasAd = cost.advertising_budget_enabled !== false;
                  const visibleCostCols = 1 + (hasExternal ? 1 : 0) + (hasAd ? 1 : 0);
                  const mdCol = visibleCostCols === 1 ? 12 : visibleCostCols === 2 ? 6 : 4;

                  return (
                    <>
                      {/* Row: Margin %, Cost, Quantity, Remove (same line) */}
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Description"
                            multiline
                            minRows={1}
                            maxRows={6}
                            value={cost.description || ''}
                            onChange={(e) => {
                              const costIndex = formData.costs.findIndex(c => c === cost);
                              handleCostChange(costIndex, 'description', e.target.value);
                            }}
                            disabled={isViewOnly}
                          />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <NumericField
                            fullWidth
                            label="Margin %"
                            value={cost.margin_percent}
                            onValueChange={(n) => {
                              const costIndex = formData.costs.findIndex(c => c === cost);
                              handleCostChange(costIndex, 'margin_percent', n);
                            }}
                            mode="decimal"
                            decimals={1}
                            suffix="%"
                            min={-100}
                            max={500}
                            disabled={isViewOnly}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <NumericField
                            fullWidth
                            label="Cost"
                            value={cost.internal_cost}
                            onValueChange={(n) => {
                              const costIndex = formData.costs.findIndex(c => c === cost);
                              handleCostChange(costIndex, 'internal_cost', n);
                            }}
                            mode="decimal"
                            decimals={2}
                            min={0}
                            prefix="$"
                            disabled={isViewOnly}
                          />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <NumericField
                            fullWidth
                            label="Quantity"
                            value={cost.quantity}
                            onValueChange={(n) => {
                              const costIndex = formData.costs.findIndex(c => c === cost);
                              handleCostChange(costIndex, 'quantity', n);
                            }}
                            mode="decimal"
                            decimals={2}
                            min={1}
                            disabled={isViewOnly}
                          />
                        </Grid>
                        {hasExternal && (
                          <Grid item xs={12} md={2}>
                            <NumericField
                              fullWidth
                              label="External Fee"
                              value={cost.external_fee}
                              onValueChange={(n) => {
                                const costIndex = formData.costs.findIndex(c => c === cost);
                                handleCostChange(costIndex, 'external_fee', n);
                              }}
                              mode="decimal"
                              decimals={2}
                              min={0}
                              prefix="$"
                              disabled={isViewOnly}
                            />
                          </Grid>
                        )}
                        {hasAd && (
                          <Grid item xs={12} md={2}>
                            <NumericField
                              fullWidth
                              label="Advertising Budget"
                              value={cost.advertising_budget}
                              onValueChange={(n) => {
                                const costIndex = formData.costs.findIndex(c => c === cost);
                                handleCostChange(costIndex, 'advertising_budget', n);
                              }}
                              mode="decimal"
                              decimals={2}
                              min={0}
                              prefix="$"
                              disabled={isViewOnly}
                            />
                          </Grid>
                        )}
                        <Grid item xs={12} md={1}>
                          <IconButton
                            color="error"
                            onClick={() => {
                              const costIndex = formData.costs.findIndex(c => c === cost);
                              handleRemoveCost(costIndex);
                            }}
                            disabled={isViewOnly}
                          >
                            <RemoveIcon />
                          </IconButton>
                        </Grid>
                      </Grid>

                      {/* Row: external/ad frequency chips aligned right */}
                      {(hasExternal || hasAd) && (
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', rowGap: 1, columnGap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 'auto' }}>
                              {hasExternal && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>External Fee Frequency</Typography>
                                  <Box sx={{ mt: 0.5 }}>{renderFrequencyChips(cost.external_fee_frequency)}</Box>
                                </Box>
                              )}
                              {hasAd && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Ad Budget Frequency</Typography>
                                  <Box sx={{ mt: 0.5 }}>{renderFrequencyChips(cost.advertising_budget_frequency)}</Box>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                      )}

                      {/* Vendor shown read-only next to price above */}
                    </>
                  );
                };

                return (
                  <Box>
                    {groupedCosts.monthly.length > 0 && (
                      <Card sx={{ mb: 2 }}>
                        <CardContent>
                          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                            <ScheduleIcon sx={{ mr: 1, color: 'primary.main' }} />
                            Monthly Costs
                          </Typography>
                          {groupedCosts.monthly.map((cost, index) => (
                            <React.Fragment key={index}>
                            {index > 0 && <Divider sx={{ my: 2, borderBottomWidth: 2 }} />}
                            <Box
                              ref={cost === justAddedCost ? justAddedCostRef : undefined}
                              sx={{
                                p: 2,
                                bgcolor: 'grey.50',
                                borderRadius: 1,
                                transition: 'box-shadow 300ms',
                                boxShadow: (theme) => (cost === justAddedCost ? `0 0 0 2px ${theme.palette.primary.main}` : 'none')
                              }}
                            >
                              {renderCostHeader(cost)}
                              <Typography variant="subtitle1">
                                {(() => (entities.find(e => e.id === cost.entity_id)?.entityName) || '—')()}
                              </Typography>
                              {renderEditableCostRow(cost)}
                            </Box>
                            </React.Fragment>
                          ))}
                        </CardContent>
                      </Card>
                    )}

                    {groupedCosts.oneTime.length > 0 && (
                      <Card sx={{ mb: 2 }}>
                        <CardContent>
                          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                            <ServiceIcon sx={{ mr: 1, color: 'primary.main' }} />
                            One-Time Costs
                          </Typography>
                          {groupedCosts.oneTime.map((cost, index) => (
                            <React.Fragment key={index}>
                            {index > 0 && <Divider sx={{ my: 2, borderBottomWidth: 2 }} />}
                            <Box
                              ref={cost === justAddedCost ? justAddedCostRef : undefined}
                              sx={{
                                p: 2,
                                bgcolor: 'grey.50',
                                borderRadius: 1,
                                transition: 'box-shadow 300ms',
                                boxShadow: (theme) => (cost === justAddedCost ? `0 0 0 2px ${theme.palette.primary.main}` : 'none')
                              }}
                            >
                              {renderCostHeader(cost)}
                              <Typography variant="subtitle1">
                                {(() => (entities.find(e => e.id === cost.entity_id)?.entityName) || '—')()}
                              </Typography>
                              {renderEditableCostRow(cost)}
                            </Box>
                            </React.Fragment>
                          ))}
                        </CardContent>
                      </Card>
                    )}

                    {/* {(groupedCosts.monthly.length > 0 || groupedCosts.oneTime.length > 0) && (
                      <Card sx={{ mb: 2, bgcolor: 'primary.50' }}>
                        <CardContent>
                          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                            <PercentIcon sx={{ mr: 1, color: 'primary.main' }} />
                            Company Margin (Auto-computed)
                          </Typography>
                          <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                            <Typography variant="h5" color="primary.main" fontWeight="bold">
                              Total Company Margin: {formatCurrency(totalMargin)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              This represents the total margin across all costs and will be used for revenue allocation in downstream billing.
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    )} */}

                    {formData.costs.filter(c => !c.isNew).length === 0 && (
                      <Alert severity="info">
                        Add at least one service to create a proposal.
                      </Alert>
                    )}
                  </Box>
                );
              })()}
            </Box>
          )}

          {dialogTab === 2 && (
            <Box>
              <Divider sx={{ my: 2 }} />
              <PartnerRolesEditor
                roles={formData.partnerRoles}
                entities={entities}
                disabled={isViewOnly}
                onChange={(partnerRoles) => setFormData((prev) => ({ ...prev, partnerRoles }))}
                onNotify={({ message, severity }) => setSnackbar({ open: true, message, severity })}
              />
             
            </Box>
          )}

          {dialogTab === 3 && (
            <Box>
              <Divider sx={{ my: 2 }} />
              {(() => {
                const { services, totals } = getFinancialBreakdown(formData.costs, formData.partnerRoles);
                return (
                  <Box>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} md={6}>
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle2" color="text.secondary">First Payment Total</Typography>
                            <Typography variant="h5" color="primary" fontWeight="bold">
                              {formatCurrency(totals.firstPaymentTotal)}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle2" color="text.secondary">Monthly Payments Total</Typography>
                            <Typography variant="h5" color="primary" fontWeight="bold">
                              {formatCurrency(totals.monthlyTotal)}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>

                    <Paper sx={{ width: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                      <TableContainer sx={{ maxHeight: '50vh', flex: 1, minHeight: 0 }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Service</TableCell>
                              <TableCell>Frequency</TableCell>
                              <TableCell align="right">First Payment</TableCell>
                              <TableCell align="right">Monthly</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {services.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={4} align="center">
                                  <Typography variant="body2" color="text.secondary">No services selected.</Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              services.map((s, idx) => (
                                <TableRow key={idx} hover>
                                  <TableCell>{s.name}</TableCell>
                                  <TableCell>
                                  <Chip
                                    size="small"
                                    color={s.frequency === 'monthly' ? 'info' : 'success'}
                                    label={s.frequency === 'monthly' ? 'Monthly' : 'One-time'}
                                  />
                                  </TableCell>
                                  <TableCell align="right">{formatCurrency(s.firstPaymentAmount)}</TableCell>
                                  <TableCell align="right">{formatCurrency(s.monthlyAmount)}</TableCell>
                                </TableRow>
                              ))
                            )}
                            {services.length > 0 && (
                              <TableRow>
                                <TableCell colSpan={2} align="right"><Typography fontWeight="bold">Totals</Typography></TableCell>
                                <TableCell align="right"><Typography fontWeight="bold">{formatCurrency(totals.firstPaymentTotal)}</Typography></TableCell>
                                <TableCell align="right"><Typography fontWeight="bold">{formatCurrency(totals.monthlyTotal)}</Typography></TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Box>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
          {!isViewOnly && (
            <>
              <Button 
                onClick={handleSave} 
                variant="outlined" 
                disabled={saving}
              >
                {saving ? <CircularProgress size={24} /> : 'Save'}
              </Button>
              <Button 
                onClick={handleSubmit} 
                variant="contained" 
                disabled={formData.costs.filter(c => !c.isNew).length === 0 || saving}
              >
                {saving ? <CircularProgress size={24} /> : (editingProposal ? 'Update' : 'Create')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Summary Cards */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
              Displayed Proposals
              </Typography>
              <Typography variant="h4">
                {filteredProposals.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Accepted & Contracted
              </Typography>
              <Typography variant="h4">
                {acceptedAndContractedCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Potential
              </Typography>
              <Typography variant="h4">
                {formatCurrency(totalPotential)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Acceptance Rate
              </Typography>
              <Typography variant="h4">
                {acceptanceRate}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Backdrop for loading */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading || saving}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
}
